// src/components/Languages/NewLanguageModal.js
import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createLanguage, fetchLanguages } from "../../redux/slices/languagesSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import { XMarkIcon, DocumentArrowUpIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "../../contexts/LanguageContext";

const NewLanguageModal = ({ isOpen, setIsOpen }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { loading } = useSelector((state) => state.languages);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    key: "",
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/json") {
        setErrors((prev) => ({ ...prev, file: t("language.error.fileTypeJson") }));
        return;
      }
      setSelectedFile(file);
      setErrors((prev) => ({ ...prev, file: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = t("language.error.nameRequired");
    }
    if (!formData.key.trim()) {
      newErrors.key = t("language.error.keyRequired");
    } else if (!/^[a-z]{2,5}$/.test(formData.key)) {
      newErrors.key = t("language.error.keyFormat");
    }
    if (!selectedFile) {
      newErrors.file = t("language.error.fileRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("key", formData.key);
    formDataToSend.append("file", selectedFile);

    try {
      await dispatch(createLanguage(formDataToSend)).unwrap();
      dispatch(showNotification({ message: t("language.msg.createdSuccess"), type: "success" }));
      dispatch(fetchLanguages({ currentPage: 1, limit: 10 }));
      handleClose();
    } catch (error) {
      dispatch(showNotification({ message: error || t("language.msg.failedCreate"), type: "error" }));
    }
  };

  const handleClose = () => {
    setFormData({ name: "", key: "" });
    setSelectedFile(null);
    setErrors({});
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-dashboardDark">{t("language.modal.addNew")}</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Language Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("language.field.name")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t("language.field.namePlaceholder")}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-dashboardCoral ${
                errors.name ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Language Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("language.field.key")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="key"
              value={formData.key}
              onChange={handleChange}
              placeholder={t("language.field.keyPlaceholder")}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-dashboardCoral ${
                errors.key ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.key && (
              <p className="text-red-500 text-sm mt-1">{errors.key}</p>
            )}
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("language.field.file")} <span className="text-red-500">*</span>
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                errors.file
                  ? "border-red-500 bg-red-50"
                  : selectedFile
                  ? "border-green-500 bg-green-50"
                  : "border-gray-300 hover:border-dashboardCoral"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                onChange={handleFileChange}
                className="hidden"
              />
              <DocumentArrowUpIcon className="w-10 h-10 mx-auto text-gray-400 mb-2" />
              {selectedFile ? (
                <p className="text-green-600 font-medium">{selectedFile.name}</p>
              ) : (
                <p className="text-gray-500">{t("language.field.uploadPrompt")}</p>
              )}
            </div>
            {errors.file && (
              <p className="text-red-500 text-sm mt-1">{errors.file}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {t("language.button.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-dashboardCoral text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
            >
              {loading ? t("language.button.creating") : t("language.button.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewLanguageModal;
